import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Bot, Check, CheckCheck, RefreshCw, SkipForward } from "lucide-react";
import {
  formatMoney,
  ReceiptListItem,
  receipts,
  review,
  ReviewDetail,
  ReviewFinding,
  ReviewFix,
  ReviewGroup,
  RULE_FIELDS,
} from "../api/client";
import StatusPill from "../components/b/StatusPill";
import { formatReceiptDate } from "../receiptDate";

const FIELD_LABEL: Record<string, string> = {
  receipt_type: "type",
  total_cents: "total",
  tax_cents: "tax",
  currency: "currency",
  invoice_date: "date",
  due_date: "due date",
  invoice_number: "invoice #",
  card_brand: "card brand",
  card_last4: "card",
};

const TYPE_LABEL: Record<string, string> = { receipt: "Receipt", bill: "Bill", income: "Income" };

function fixValue(fix: ReviewFix, r: ReceiptListItem): string {
  if (fix.field === "total_cents" || fix.field === "tax_cents") return formatMoney(Number(fix.value), r.currency);
  if (fix.field === "receipt_type") return TYPE_LABEL[String(fix.value)] ?? String(fix.value);
  return String(fix.value);
}

const sameFix = (a: ReviewFix | null, b: ReviewFix) => !!a && a.field === b.field && String(a.value) === String(b.value);

const groupKey = (g: { kind: string; vendor_name: string }) => `${g.kind}:${g.vendor_name}`;
const displayName = (name: string) => name || "(no name)";

function GroupBadge({ g }: { g: ReviewGroup }) {
  if (g.status === "pending" || g.status === "processing") return <span className="review-badge">Checking…</span>;
  if (g.status === "none") return <span className="review-badge">Not checked</span>;
  if (g.status === "error") return <span className="review-badge is-error">Check failed</span>;
  if (g.flagged_count > 0)
    return <span className={"review-badge " + (g.high_count ? "is-high" : "is-warn")}>{g.flagged_count} flagged</span>;
  return <span className="review-badge is-ok">{g.stale ? "Changed" : "Looks fine"}</span>;
}

export default function Review() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [groups, setGroups] = useState<ReviewGroup[] | null>(null);
  const [detail, setDetail] = useState<ReviewDetail | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const current = useMemo(() => {
    if (!groups?.length) return null;
    const key = params.get("g");
    if (key) return groups.find((g) => groupKey(g) === key) ?? null; // finished or gone: pick another
    return groups[0];
  }, [groups, params]);

  const loadGroups = useCallback(async () => {
    try {
      setGroups(await review.list());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load");
    }
  }, []);

  const loadDetail = useCallback(async (g: ReviewGroup | null) => {
    if (!g) return setDetail(null);
    try {
      setDetail(await review.group(g.kind, g.vendor_name));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load");
    }
  }, []);

  // Queue checks for anything new or changed, then show the list.
  useEffect(() => {
    review.check().catch(() => undefined).finally(loadGroups);
  }, [loadGroups]);

  const currentKey = current ? groupKey(current) : null;
  useEffect(() => setSelected(new Set()), [currentKey]);
  useEffect(() => {
    loadDetail(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, current?.status, current?.checked_at, loadDetail]);

  // While the AI works, refresh every few seconds.
  const working = groups?.some((g) => g.status === "pending" || g.status === "processing");
  useEffect(() => {
    if (!working) return;
    const t = setInterval(loadGroups, 4000);
    return () => clearInterval(t);
  }, [working, loadGroups]);

  function open(g: ReviewGroup | null) {
    setNotice(null);
    setError(null);
    const next = new URLSearchParams(params);
    if (g) next.set("g", groupKey(g));
    else next.delete("g");
    setParams(next, { replace: true });
  }

  function nextGroup(): ReviewGroup | null {
    if (!groups || !current) return null;
    const i = groups.findIndex((g) => groupKey(g) === groupKey(current));
    return groups[(i + 1) % groups.length] ?? null;
  }

  async function run(action: () => Promise<string | void>) {
    setBusy(true);
    setError(null);
    try {
      const message = await action();
      setNotice(message || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  const pending = detail?.receipts ?? [];
  const allSelected = pending.length > 0 && pending.every((r) => selected.has(r.id));
  const byReceipt = useMemo(() => {
    const m = new Map<string, ReviewFinding[]>();
    for (const f of detail?.findings ?? []) m.set(f.receipt_id, [...(m.get(f.receipt_id) ?? []), f]);
    return m;
  }, [detail]);
  // Receipts with findings first (in the AI's severity order), then the rest newest first.
  const rows = useMemo(() => {
    const order = [...new Set(detail?.findings.map((f) => f.receipt_id))];
    const flagged = order.map((id) => pending.find((r) => r.id === id)).filter(Boolean) as ReceiptListItem[];
    return [...flagged, ...pending.filter((r) => !order.includes(r.id))];
  }, [detail, pending]);

  async function markReviewed(ids: string[]) {
    if (!current || ids.length === 0) return;
    await run(async () => {
      const res = await receipts.bulkStatus(ids, "reviewed");
      const left = pending.filter((r) => !ids.includes(r.id)).length;
      const fresh = await review.list();
      setGroups(fresh);
      setSelected(new Set());
      if (left === 0) {
        // Everything here is reviewed: on to the next vendor.
        const i = groups?.findIndex((g) => groupKey(g) === groupKey(current)) ?? 0;
        const next = fresh[Math.min(i, fresh.length - 1)] ?? null;
        open(next);
      } else {
        await loadDetail(current);
      }
      return `Marked ${res.updated} item${res.updated === 1 ? "" : "s"} as reviewed.`;
    });
  }

  async function applyFix(r: ReceiptListItem, fix: ReviewFix) {
    await run(async () => {
      await receipts.update(r.id, { [fix.field]: fix.value });
      await Promise.all([loadDetail(current), loadGroups()]);
      return `Set ${FIELD_LABEL[fix.field]} to ${fixValue(fix, r)}.`;
    });
  }

  async function applyAlways(r: ReceiptListItem, fix: ReviewFix) {
    if (!current) return;
    const ids = (detail?.findings ?? []).filter((f) => sameFix(f.fix, fix)).map((f) => f.receipt_id);
    const what = `${FIELD_LABEL[fix.field]} to ${fixValue(fix, r)}`;
    const scope = ids.length > 1 ? `these ${ids.length} flagged receipts` : "this receipt";
    if (!window.confirm(`Set ${what} on ${scope} and on every new receipt from “${displayName(current.vendor_name)}”?\n\nYou can remove the rule in Settings.`))
      return;
    await run(async () => {
      await review.always({ kind: current.kind, vendor_name: current.vendor_name, field: fix.field,
                            value: String(fix.value), receipt_ids: ids });
      await Promise.all([loadDetail(current), loadGroups()]);
      return `Saved: new receipts from ${displayName(current.vendor_name)} get ${what}.`;
    });
  }

  async function recheck() {
    if (!current) return;
    await run(async () => {
      await review.check({ kind: current.kind, vendor_name: current.vendor_name });
      await loadGroups();
    });
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const checking = detail && (detail.status === "pending" || detail.status === "processing");
  const entityPath = current
    ? `/app/${current.kind === "client" ? "clients" : "vendors"}/name:${encodeURIComponent(current.vendor_name)}`
    : "";

  return (
    <div className="page">
      <div className="page-header">
        <h1>Bulk Review</h1>
        <p className="page-sub">
          The AI looks over each vendor's and client's receipts and flags what needs you. Fix those, tick what you've
          checked and mark it reviewed.
        </p>
      </div>

      {error && <div className="page-error">{error}</div>}
      {notice && <div className="page-toast">{notice}</div>}

      {groups === null ? (
        error ? null : <div className="page-empty">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="page-empty">
          <p>Nothing left to review.</p>
        </div>
      ) : (
        <div className="review-layout">
          <nav className="review-groups" aria-label="Vendors and clients to review">
            {groups.map((g) => (
              <button
                key={groupKey(g)}
                className={"review-group" + (current && groupKey(g) === groupKey(current) ? " active" : "")}
                onClick={() => open(g)}
              >
                <span className="review-group-name">{displayName(g.vendor_name)}</span>
                <span className="review-group-meta">
                  {g.kind === "client" ? "Client" : "Vendor"} · {g.pending_count} to review
                  {g.sample && <span className="sample-chip">Sample</span>}
                </span>
                <GroupBadge g={g} />
              </button>
            ))}
          </nav>

          {!current && (
            <section className="review-pane">
              <div className="page-empty">That one is all reviewed. Pick a vendor or client on the left.</div>
            </section>
          )}
          {current && detail && groupKey(detail) === groupKey(current) && (
            <section className="review-pane">
              <div className="review-head">
                <div>
                  <h2>
                    {current.vendor_name ? <Link to={entityPath}>{current.vendor_name}</Link> : displayName("")}
                  </h2>
                  <span className="muted">
                    {current.kind === "client" ? "Client" : "Vendor"} · {pending.length} of {detail.total_count} need
                    review
                  </span>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => open(nextGroup())} disabled={busy}>
                  <SkipForward size={14} /> Skip
                </button>
              </div>

              <div className={"review-ai" + (detail.status === "error" ? " is-error" : "")}>
                <Bot size={18} />
                <div>
                  {checking ? (
                    <span>The AI is checking these receipts…</span>
                  ) : detail.status === "none" ? (
                    <span>Not checked yet.</span>
                  ) : detail.status === "error" ? (
                    <span>The check failed: {detail.error}</span>
                  ) : (
                    <>
                      <span>{detail.summary || "Nothing stands out."}</span>
                      {detail.findings.length === 0 && <strong> Nothing needs your attention.</strong>}
                      {detail.stale && <span className="muted"> Receipts changed since this check.</span>}
                    </>
                  )}
                </div>
                {!checking && (detail.status !== "done" || detail.stale) && (
                  <button className="btn btn-ghost btn-sm" onClick={recheck} disabled={busy}>
                    <RefreshCw size={14} /> {detail.status === "none" ? "Check now" : "Re-check"}
                  </button>
                )}
              </div>

              <div className="bulk-bar">
                <button
                  className="btn btn-primary btn-sm"
                  data-tour="bulk-review"
                  onClick={() => markReviewed([...selected])}
                  disabled={busy || selected.size === 0}
                >
                  <CheckCheck size={14} />
                  Mark {selected.size} item{selected.size === 1 ? "" : "s"} as reviewed
                </button>
                {selected.size === 0 && <span className="muted">Tick the items you've checked.</span>}
              </div>

              <table className="data-table review-table">
                <thead>
                  <tr>
                    <th className="check-col">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        checked={allSelected}
                        onChange={() => setSelected(allSelected ? new Set() : new Set(pending.map((r) => r.id)))}
                      />
                    </th>
                    <th>Date</th>
                    <th>Subject</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th>Type</th>
                    <th>Card</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const findings = byReceipt.get(r.id) ?? [];
                    return (
                      <FindingRow
                        key={r.id}
                        r={r}
                        findings={findings}
                        selected={selected.has(r.id)}
                        busy={busy}
                        vendorName={displayName(current.vendor_name)}
                        kindLabel={current.kind === "client" ? "client" : "vendor"}
                        firstFinding={r.id === detail.findings[0]?.receipt_id}
                        canSaveRule={!!current.vendor_name}
                        onToggle={() => toggle(r.id)}
                        onOpen={() => navigate(`/app/receipts/${r.id}`)}
                        onApply={(fix) => applyFix(r, fix)}
                        onAlways={(fix) => applyAlways(r, fix)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function FindingRow(props: {
  r: ReceiptListItem;
  findings: ReviewFinding[];
  selected: boolean;
  busy: boolean;
  vendorName: string;
  kindLabel: string;
  firstFinding: boolean;
  canSaveRule: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onApply: (fix: ReviewFix) => void;
  onAlways: (fix: ReviewFix) => void;
}) {
  const { r, findings } = props;
  const card = [r.card_brand, r.card_last4 && `···· ${r.card_last4}`].filter(Boolean).join(" ");
  return (
    <>
      <tr className={"clickable" + (props.selected ? " selected" : "") + (findings.some((f) => !f.fixed) ? " flagged" : "")}
          onClick={props.onOpen}>
        <td className="check-col" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" aria-label="Select" checked={props.selected} onChange={props.onToggle} />
        </td>
        <td style={{ whiteSpace: "nowrap" }}>{formatReceiptDate(r)}</td>
        <td className="review-subject">{r.subject || r.description || "(no subject)"}</td>
        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {formatMoney(r.amount_cents, r.currency)}
        </td>
        <td><StatusPill status={r.receipt_type} /></td>
        <td style={{ whiteSpace: "nowrap" }}>{card || "—"}</td>
      </tr>
      {findings.map((f, i) => (
        <tr key={i} className={"review-finding" + (f.fixed ? " is-fixed" : "")} onClick={props.onOpen}
            data-tour={props.firstFinding && i === 0 ? "finding" : undefined}>
          <td />
          <td colSpan={5}>
            <div className="review-finding-body" onClick={(e) => e.stopPropagation()}>
              <span className="review-issue">{f.issue}</span>
              {f.fix &&
                (f.fixed ? (
                  <span className="review-fixed"><Check size={14} /> Fixed</span>
                ) : (
                  <span className="review-actions">
                    <button className="btn btn-ghost btn-sm" disabled={props.busy} onClick={() => props.onApply(f.fix!)}>
                      Set {FIELD_LABEL[f.fix.field]} to {fixValue(f.fix, r)}
                    </button>
                    {props.canSaveRule && RULE_FIELDS.includes(f.fix.field) && (
                      <button className="btn btn-ghost btn-sm" disabled={props.busy}
                              title={`Also apply to every new receipt from ${props.vendorName}`}
                              onClick={() => props.onAlways(f.fix!)}>
                        Always for this {props.kindLabel}
                      </button>
                    )}
                  </span>
                ))}
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}
